import { ArrowLeft, LogOut, MapPin, SlidersHorizontal, X } from 'lucide-react';
import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSwipeable } from 'react-swipeable';
import Banner from '../components/feedback/Banner';
import { LanguageSelector } from '../components/LanguageSelector';
import Layout from '../components/Layout';
import GuidebookPageBody, { GuidebookPage } from '../components/guidebook/GuidebookPageBody';
import { UnifiedMap } from '../components/maps';
import { useLanguage } from '../contexts/LanguageContext';
import { useReservation } from '../contexts/ReservationContext';
import { guidebooksApi } from '../lib/api';
import { PIN_CATEGORIES } from '../lib/pin-categories';

// Use the proper types from the API response
interface GuidebookSection {
  id: string;
  name: string;
  icon: string;
  order: number;
  pages: GuidebookPage[];
}

function isLucideKey(v: string | null | undefined): boolean {
  return !!v && /^[A-Z][A-Za-z0-9]*$/.test(v);
}

async function loadLucide(name: string) {
  const mod = await import('lucide-react');
  return (mod as any)[name] as React.ComponentType<any> | undefined;
}

type View = 'sections' | 'page';

export default function Guidebook() {
  const [searchParams] = useSearchParams();
  const previewListingId = searchParams.get('listingId');

  const [view, setView] = React.useState<View>('sections');
  const [sections, setSections] = React.useState<GuidebookSection[]>([]);
  const [selectedPageId, setSelectedPageId] = React.useState<string | null>(null);
  const [expandedSections, setExpandedSections] = React.useState<Set<string>>(new Set());
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [iconCache, setIconCache] = React.useState<Record<string, React.ComponentType<any>>>({});
  const [showMapModal, setShowMapModal] = React.useState(false);
  const [selectedFilters, setSelectedFilters] = React.useState<Set<string>>(new Set());
  const [showFilterDropdown, setShowFilterDropdown] = React.useState(false);

  // Checkout state
  const [checkoutEnabled, setCheckoutEnabled] = React.useState(false);
  const [checkoutCompleted, setCheckoutCompleted] = React.useState(false);
  const [checkoutLoading, setCheckoutLoading] = React.useState(false);
  const [showCheckoutConfirm, setShowCheckoutConfirm] = React.useState(false);
  const [checkoutJustCompleted, setCheckoutJustCompleted] = React.useState(false);
  const [checkoutError, setCheckoutError] = React.useState(false);

  const { language } = useLanguage();

  // Get reservation context - provides googleMapsApiKey for both authenticated and preview modes
  const { summary } = useReservation();
  const googleMapsApiKey = summary?.googleMapsApiKey || '';

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);

        // Fetch guidebook data - preview mode uses listingId from URL, authenticated mode uses auth token
        const ui = previewListingId
          ? await guidebooksApi.getUiPreview(parseInt(previewListingId), language)
          : await guidebooksApi.getUi(language);
        if (cancelled) return;
        setSections(ui.sections || []);
        setCheckoutEnabled(ui.checkoutEnabled || false);
        setCheckoutCompleted(ui.checkoutCompleted || false);
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.message || 'Failed to load guidebook');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [previewListingId, language]);

  const renderIcon = (icon: string | null, size: 'sm' | 'md' | 'lg' = 'md') => {
    const sizeClasses = {
      sm: 'w-5 h-5',
      md: 'w-8 h-8',
      lg: 'w-10 h-10',
    };
    const fontSize = {
      sm: 'text-xl',
      md: 'text-3xl',
      lg: 'text-4xl',
    };

    if (!icon) return <span className={fontSize[size]}>📄</span>;
    if (!isLucideKey(icon)) return <span className={fontSize[size]}>{icon}</span>;
    const Cmp = iconCache[icon];
    if (Cmp) return <Cmp className={sizeClasses[size]} strokeWidth={2} />;
    loadLucide(icon).then((c) => c && setIconCache((cache) => ({ ...cache, [icon]: c })));
    return <div className={sizeClasses[size]} />;
  };

  const toggleSection = (sectionId: string) => {
    const section = sections.find((s) => s.id === sectionId);
    if (!section) return;

    // If section has only 1 page, navigate directly to it
    if (section.pages.length === 1) {
      handlePageClick(section.pages[0].id);
      return;
    }

    // Otherwise, toggle expansion
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };

  const handlePageClick = (pageId: string) => {
    setSelectedPageId(pageId);
    setView('page');
  };

  const handleBackToSections = () => {
    setView('sections');
    setSelectedPageId(null);
  };

  // Swipe right to go back (mobile gesture) with iOS-like animation
  const [swipeOffset, setSwipeOffset] = React.useState(0);
  const [isSwiping, setIsSwiping] = React.useState(false);
  const swipeThreshold = 100; // Minimum distance to trigger back navigation

  // Refs to avoid stale closure issues in swipe callbacks
  const swipeOffsetRef = React.useRef(0);
  const isSwipingRef = React.useRef(false);
  const isAnimatingRef = React.useRef(false); // Prevent new swipes during animation
  const animationTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
    };
  }, []);

  const swipeHandlers = useSwipeable({
    onSwiping: (eventData) => {
      // Don't allow new swipes during animation
      if (isAnimatingRef.current) return;

      // Start swipe only from left edge (first 30px) going right
      const isValidStart = eventData.initial[0] < 30 && view === 'page';

      // If this is the start of a valid swipe (from left edge, going right)
      if (isValidStart && !isSwipingRef.current && eventData.dir === 'Right') {
        isSwipingRef.current = true;
        setIsSwiping(true);
      }

      // Once swiping has started, continue tracking regardless of direction
      if (isSwipingRef.current) {
        // Limit the offset to screen width, clamp to 0 minimum
        const maxOffset = window.innerWidth;
        const offset = Math.min(eventData.deltaX, maxOffset);
        const clampedOffset = Math.max(0, offset);
        swipeOffsetRef.current = clampedOffset;
        setSwipeOffset(clampedOffset);
      }
    },
    onSwiped: () => {
      if (!isSwipingRef.current) return;

      // If swiped past threshold, complete the navigation
      if (swipeOffsetRef.current > swipeThreshold) {
        // Lock to prevent new swipes during animation
        isAnimatingRef.current = true;
        // First disable swiping to enable the CSS transition
        isSwipingRef.current = false;
        setIsSwiping(false);
        // Then animate off-screen (transition now enabled)
        setSwipeOffset(window.innerWidth);
        // After animation completes, navigate and reset
        animationTimeoutRef.current = setTimeout(() => {
          // Only proceed if still animating (not interrupted)
          if (isAnimatingRef.current) {
            handleBackToSections();
            swipeOffsetRef.current = 0;
            setSwipeOffset(0);
            isAnimatingRef.current = false;
          }
        }, 300); // Match the CSS transition duration (0.3s)
      } else {
        // Snap back to original position
        swipeOffsetRef.current = 0;
        isSwipingRef.current = false;
        setSwipeOffset(0);
        setIsSwiping(false);
      }
    },
    trackMouse: false,
    delta: 10,
    preventScrollOnSwipe: false,
  });

  const selectedPage = React.useMemo(() => {
    if (!selectedPageId) return null;
    for (const section of sections) {
      const page = section.pages.find((p) => p.id === selectedPageId);
      if (page) return page;
    }
    return null;
  }, [selectedPageId, sections]);

  // Check if there are any place pages
  const hasPlacePages = React.useMemo(() => {
    return sections.some((s) => s.pages.some((p) => p.pageType === 'place'));
  }, [sections]);

  // Get all place pages for the unified map
  const allPlaces = React.useMemo(() => {
    const places: Array<{
      id: string;
      name: string;
      lat: number;
      lng: number;
      pinStyle: string;
      photoUrl?: string;
      rating?: number;
      address: string;
    }> = [];

    sections.forEach((section) => {
      section.pages.forEach((page) => {
        if (page.pageType === 'place' && page.placeData) {
          places.push({
            id: page.id,
            name: page.placeData.name,
            lat: page.placeData.location.lat,
            lng: page.placeData.location.lng,
            pinStyle: page.placeData.pinStyle,
            photoUrl: page.placeData.photoUrl,
            rating: page.placeData.rating,
            address: page.placeData.address,
          });
        }
      });
    });

    return places;
  }, [sections]);

  // Get unique categories present in the guidebook
  const availableCategories = React.useMemo(() => {
    const categorySet = new Set(allPlaces.map((p) => p.pinStyle));
    return PIN_CATEGORIES.filter((cat) => categorySet.has(cat.value));
  }, [allPlaces]);

  // Filter places based on selected filters
  const filteredPlaces = React.useMemo(() => {
    if (selectedFilters.size === 0) {
      return allPlaces; // No filters = show all
    }
    return allPlaces.filter((place) => selectedFilters.has(place.pinStyle));
  }, [allPlaces, selectedFilters]);

  // Toggle filter
  const toggleFilter = (categoryValue: string) => {
    setSelectedFilters((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(categoryValue)) {
        newSet.delete(categoryValue);
      } else {
        newSet.add(categoryValue);
      }
      return newSet;
    });
  };

  // Memoize the marker click handler to prevent map re-initialization
  const handleMarkerClick = React.useCallback((placeId: string) => {
    setSelectedPageId(placeId);
    setView('page');
    setShowMapModal(false);
    setShowFilterDropdown(false);
  }, []);

  // Load Lucide icons for filter chips
  React.useEffect(() => {
    availableCategories.forEach((category) => {
      // Load icon if not already in cache
      // We don't include iconCache in deps because it would cause re-renders
      // Instead, we check the cache state when the icon loads
      loadLucide(category.lucideIcon).then((Icon) => {
        if (Icon) {
          setIconCache((cache) => {
            // Only update if this icon isn't already cached
            if (cache[category.lucideIcon]) {
              return cache;
            }
            return { ...cache, [category.lucideIcon]: Icon };
          });
        }
      });
    });
  }, [availableCategories]);

  if (loading) {
    return (
      <Layout showReservation={false}>
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <span className="ml-4 text-lg text-gray-600">Loading your guidebook...</span>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout showReservation={false}>
        <div className="max-w-2xl mx-auto mt-8">
          <div className="bg-red-50 border border-red-200 rounded-xl p-6">
            <p className="text-red-800 text-center">{error}</p>
          </div>
        </div>
      </Layout>
    );
  }

  const isPreview = !!previewListingId;

  const handleCheckout = async () => {
    setShowCheckoutConfirm(false);
    setCheckoutLoading(true);
    setCheckoutError(false);

    // In preview mode, just show visual feedback without inserting an event
    if (isPreview) {
      setCheckoutCompleted(true);
      setCheckoutJustCompleted(true);
      setCheckoutLoading(false);
      return;
    }

    try {
      await guidebooksApi.checkout();
      setCheckoutCompleted(true);
      setCheckoutJustCompleted(true);
    } catch (e: any) {
      // If already checked out (409), treat as success
      if (e?.status === 409) {
        setCheckoutCompleted(true);
        setCheckoutJustCompleted(true);
      } else {
        setCheckoutError(true);
        console.error('Checkout failed:', e);
      }
    } finally {
      setCheckoutLoading(false);
    }
  };

  if (!sections.length && !checkoutEnabled) {
    return (
      <Layout showReservation={true}>
        <div className="max-w-2xl mx-auto mt-8">
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-12 text-center">
            <p className="text-gray-600 text-lg">No guidebook is configured for your stay.</p>
          </div>
        </div>
      </Layout>
    );
  }

  // Render map modal (available in all views)
  const renderMapModal = () => {
    if (!showMapModal || !googleMapsApiKey) return null;

    return (
      <div className="fixed inset-0 z-50">
        {/* Full Map - No header, full screen */}
        <div className="w-full h-full">
          <UnifiedMap places={filteredPlaces} apiKey={googleMapsApiKey} onMarkerClick={handleMarkerClick} />
        </div>

        {/* Floating Controls - Top Right */}
        <div className="fixed top-4 right-4 z-[60] flex items-center gap-2">
          {/* Filter Button */}
          {availableCategories.length > 1 && (
            <div className="relative">
              <button
                onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                className={`
                  inline-flex items-center gap-2 px-3 py-2 rounded-lg shadow-lg border transition-all
                  ${showFilterDropdown ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-gray-200 text-gray-700 hover:shadow-xl'}
                `}
              >
                <SlidersHorizontal className="w-4 h-4" />
                <span className="font-medium text-sm">Filter</span>
                {selectedFilters.size > 0 && (
                  <span
                    className={`inline-flex items-center justify-center w-5 h-5 text-xs font-bold rounded-full ${showFilterDropdown ? 'bg-white text-blue-600' : 'bg-blue-600 text-white'}`}
                  >
                    {selectedFilters.size}
                  </span>
                )}
              </button>

              {/* Filter Dropdown */}
              {showFilterDropdown && (
                <>
                  {/* Backdrop */}
                  <div className="fixed inset-0 z-40" onClick={() => setShowFilterDropdown(false)} />

                  {/* Dropdown Menu */}
                  <div className="absolute right-0 top-full mt-2 w-72 bg-white rounded-lg shadow-xl border border-gray-200 z-50">
                    <div className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-semibold text-gray-900 text-sm">Filter by Category</h3>
                        {selectedFilters.size > 0 && (
                          <button
                            onClick={() => setSelectedFilters(new Set())}
                            className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                          >
                            Clear All
                          </button>
                        )}
                      </div>

                      <div className="space-y-2 max-h-96 overflow-y-auto">
                        {availableCategories.map((category) => {
                          const isSelected = selectedFilters.has(category.value);
                          const Icon = iconCache[category.lucideIcon];
                          const count = allPlaces.filter((p) => p.pinStyle === category.value).length;

                          return (
                            <label
                              key={category.value}
                              className="flex items-center gap-3 p-2 rounded-md hover:bg-gray-50 cursor-pointer transition-colors"
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleFilter(category.value)}
                                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <div
                                className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center"
                                style={{ backgroundColor: `${category.color}20` }}
                              >
                                {Icon && <Icon className="w-4 h-4" style={{ color: category.color }} />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-gray-900">{category.label}</div>
                                <div className="text-xs text-gray-500">
                                  {count} place{count !== 1 ? 's' : ''}
                                </div>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Close Button */}
          <button
            onClick={() => setShowMapModal(false)}
            className="p-2.5 bg-white hover:bg-gray-50 rounded-lg shadow-lg border border-gray-200 transition-all hover:shadow-xl"
          >
            <X className="w-5 h-5 text-gray-700" />
          </button>
        </div>
      </div>
    );
  };

  // VIEW 1: Sections List (Collapsible) - WITH reservation info
  if (view === 'sections') {
    return (
      <Layout showReservation={true}>
        <LanguageSelector />
        {/* Checkout Success Banner - matches CheckInForm/SignAgreement green banner pattern */}
        {(checkoutCompleted || checkoutJustCompleted) && (
          <Banner type="success">You have successfully checked out. We hope you enjoyed your stay!</Banner>
        )}

        <div className="space-y-3 mt-4">
          {sections.map((section) => {
            const isExpanded = expandedSections.has(section.id);
            const hasMultiplePages = section.pages.length > 1;

            return (
              <div key={section.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                {/* Section Header */}
                <button
                  onClick={() => toggleSection(section.id)}
                  className="w-full flex items-center justify-between p-5 hover:bg-gray-50 transition-colors active:bg-gray-100"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0">{renderIcon(section.icon, 'md')}</div>
                    <div className="text-left">
                      <h2 className="text-lg font-semibold text-gray-900">{section.name}</h2>
                    </div>
                  </div>
                  {hasMultiplePages && (
                    <svg
                      className={`w-6 h-6 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  )}
                </button>

                {/* Pages List (Collapsible) - only show if section has multiple pages */}
                {hasMultiplePages && isExpanded && (
                  <div className="border-t border-gray-200 bg-gray-50 p-3 space-y-2">
                    {section.pages.map((page) => (
                      <button
                        key={page.id}
                        onClick={() => handlePageClick(page.id)}
                        className="w-full text-left px-4 py-3 bg-white rounded-lg border border-gray-200 hover:border-blue-400 hover:bg-blue-50 transition-all active:scale-[0.98]"
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-gray-900">{page.title}</span>
                          <svg
                            className="w-5 h-5 text-gray-400 flex-shrink-0 ml-2"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Checkout Button - sticky at bottom for easy mobile access */}
        {checkoutEnabled && !checkoutCompleted && !checkoutJustCompleted && (
          <div className="sticky bottom-0 z-30 -mx-4 px-4 pt-4 pb-6 bg-gradient-to-t from-white via-white to-white/80">
            {checkoutError && (
              <div className="mb-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-center">
                <p className="text-red-700 text-sm font-medium">Something went wrong. Please try again.</p>
              </div>
            )}
            <button
              onClick={() => setShowCheckoutConfirm(true)}
              disabled={checkoutLoading}
              className="w-full flex items-center justify-center gap-2 py-4 px-6 bg-blue-600 text-white rounded-xl shadow-lg hover:bg-blue-700 transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed text-base sm:text-lg"
            >
              {checkoutLoading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
              ) : (
                <LogOut className="w-5 h-5" />
              )}
              <span className="font-semibold">{checkoutLoading ? 'Processing...' : 'Check Out'}</span>
            </button>
          </div>
        )}

        {/* Checkout Confirmation Modal - responsive for mobile */}
        {showCheckoutConfirm && (
          <div
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50"
            onClick={() => setShowCheckoutConfirm(false)}
          >
            <div
              className="bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:mx-4 sm:max-w-sm p-6 pb-8 sm:pb-6 animate-slide-up sm:animate-none"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Drag handle for mobile */}
              <div className="flex justify-center mb-4 sm:hidden">
                <div className="w-10 h-1 bg-gray-300 rounded-full" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Confirm Check Out</h3>
              <p className="text-gray-600 mb-6 text-sm sm:text-base">
                Are you sure you want to confirm your checkout?{' '}
                {isPreview
                  ? '(This is a preview. Clicking confirm will mark the guest as checked out but does nothing in preview mode.)'
                  : 'This action cannot be undone.'}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowCheckoutConfirm(false)}
                  className="flex-1 py-3.5 px-4 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors active:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCheckout}
                  className="flex-1 py-3.5 px-4 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors active:bg-blue-800"
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Floating Map Button - Only show if there are place pages */}
        {hasPlacePages && googleMapsApiKey && (
          <button
            onClick={() => {
              setShowMapModal(true);
              setSelectedFilters(new Set()); // Reset filters when opening map
              setShowFilterDropdown(false); // Close filter dropdown
            }}
            className="fixed bottom-6 right-6 z-50 bg-blue-600 text-white rounded-full p-4 shadow-lg hover:bg-blue-700 transition-all active:scale-95 flex items-center gap-2"
          >
            <MapPin className="w-6 h-6" />
            <span className="font-medium">Map</span>
          </button>
        )}

        {/* Render Map Modal */}
        {renderMapModal()}
      </Layout>
    );
  }

  // VIEW 2: Full Page Content - WITHOUT reservation info, just HTML from top to bottom
  if (view === 'page' && selectedPage) {
    return (
      <div {...swipeHandlers} className="relative">
        {/* Background overlay that shows during swipe */}
        {swipeOffset > 0 && (
          <div
            className="fixed inset-0 bg-black/20 z-40 pointer-events-none"
            style={{ opacity: Math.min(swipeOffset / 150, 0.5) }}
          />
        )}

        {/* Main content with swipe transform */}
        <div
          style={{
            transform: `translateX(${swipeOffset}px)`,
            transition: isSwiping ? 'none' : 'transform 0.3s ease-out',
          }}
        >
          <Layout showReservation={false} hideTitle={true}>
            {/* Sticky Header with Back Button and Title - Always visible when scrolling */}
            <div className="sticky top-0 z-20 -mx-4 px-4 py-4 bg-white/95 backdrop-blur-sm border-b border-gray-200 mb-6">
              <div className="flex items-center justify-between gap-4">
                {/* Back Button - Left aligned */}
                <button
                  onClick={handleBackToSections}
                  className="flex items-center gap-2 text-gray-900 hover:text-gray-700 font-medium transition-colors active:scale-95 flex-shrink-0"
                >
                  <ArrowLeft className="w-5 h-5" />
                  Back
                </button>

                {/* Page Title - Centered */}
                <h1 className="text-base sm:text-lg font-semibold text-gray-900 text-center flex-1 min-w-0 leading-tight">
                  {selectedPage.title}
                </h1>

                {/* Spacer to balance the back button width */}
                <div className="w-[68px] flex-shrink-0" />
              </div>
            </div>

            {/* Page Content */}
            <GuidebookPageBody
              page={selectedPage}
              googleMapsApiKey={googleMapsApiKey}
              showMap
              onMapClick={() => setShowMapModal(true)}
            />
          </Layout>
        </div>

        {/* Render Map Modal - Outside transform wrapper to preserve position:fixed */}
        {renderMapModal()}
      </div>
    );
  }

  return null;
}
